import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { storage } from '../utils/firebase';
import { v4 as uuidv4 } from 'uuid';

const ImageUploader = ({ label, onUpload, multiple = false, storagePath }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const sanitizeFileName = (fileName) => {
    // Remove special characters and spaces that could cause URL encoding issues
    return fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  };

  const handleFileChange = async (e) => {
    const files = multiple ? Array.from(e.target.files) : [e.target.files[0]];
    if (!files.length) return;

    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setError("You must be logged in to upload images.");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      console.log('📤 Starting image upload...', multiple ? `(${files.length} files)` : '(single file)');
      
      const uploadPromises = files.map(async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error("Please select an image file.");
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error("File size must be less than 5MB.");
        }

        // Create a safe file name
        const safeFileName = sanitizeFileName(file.name);
        // Use the provided storagePath or fallback to images/
        const basePath = storagePath ? storagePath.replace(/\/$/, '') : 'images';
        const fileRef = ref(storage, `${basePath}/${uuidv4()}-${safeFileName}`);
        
        console.log('📁 Uploading to:', fileRef.fullPath);
        
        // Upload the file
        const snapshot = await uploadBytes(fileRef, file);
        console.log('✅ Upload successful:', snapshot);
        
        // Get the download URL
        const url = await getDownloadURL(fileRef);
        console.log('🔗 Download URL:', url);
        
        return url;
      });

      const urls = await Promise.all(uploadPromises);
      
      if (multiple) {
        onUpload(urls); // Pass array of URLs
      } else {
        onUpload(urls[0]); // Pass single URL
      }
      
    } catch (err) {
      console.error('❌ Upload error:', err);
      setError("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mb-2">
      <label className="block font-medium mb-1">{label}</label>
      <input 
        type="file" 
        accept="image/*" 
        onChange={handleFileChange}
        multiple={multiple}
        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
      />
      {uploading && (
        <div className="mt-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 inline-block mr-2"></div>
          <span className="text-sm text-gray-500">Uploading...</span>
        </div>
      )}
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default ImageUploader;
